import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { unscopedKey } from "@/lib/queryClient";
import { useAuthStore } from "@/stores/auth";
import type { Match } from "@/lib/types";
import { useChunkedUpload, type ChunkedUploadState } from "./useChunkedUpload";

/**
 * useUploadQueue — sequential, supervision-free multi-upload queue.
 *
 * The single-file `CoachMatchUploadModal` makes an admin babysit one
 * upload at a time. When a team has a stack of recorded videos on a
 * local disk, they want to assign several files to several matches /
 * slots and let them upload back-to-back. This hook owns the ordered
 * list and a one-at-a-time driver that delegates each upload to the
 * existing `useChunkedUpload` engine (POST /session → PUT /chunk × N →
 * POST /complete), so the chunked pipeline — including PR #90's
 * cross-reload resume — is reused unchanged.
 *
 * Concurrency: strictly one upload runs at a time. The driver only
 * starts the next `pending` item once the active one reaches a
 * terminal state.
 *
 * Persistence (best-effort resume): the list is mirrored to
 * localStorage MINUS the `File` objects (a File can't be serialized).
 * After a reload every rehydrated item has `file: null` and is flagged
 * "needs file" — the operator re-picks the file and the driver picks it
 * up. An item that was mid-upload is demoted to `pending`; its in-flight
 * chunk session resumes via the per-`{matchId,slot}` `tktic.upload-session.*`
 * entry once `useChunkedUpload.start()` runs (it calls POST /session,
 * which the server reuses when the {match, slot, size, ext, hash}
 * fingerprint matches an active session).
 */

export type QueueItemStatus =
  "pending" | "uploading" | "done" | "error" | "cancelled";

export type UploadSlot =
  "full" | "first_half" | "second_half" | "extra_time" | "penalty_shootout";

export interface QueueItem {
  id: string;
  matchId: string;
  teamId?: string;
  seasonId?: string;
  /** "Home vs Away · 2026-05-12" — display only. */
  matchLabel: string;
  slot: UploadSlot;
  /** null after a reload until the operator re-picks the file. */
  file: File | null;
  fileName: string;
  fileSize: number;
  status: QueueItemStatus;
  error: string | null;
  /** epoch ms — used to age out finished items on rehydrate. */
  updatedAt: number;
}

/** Persisted shape: everything except the non-serializable `File`. */
type StoredQueueItem = Omit<QueueItem, "file">;

/** The localStorage key is scoped per authenticated user. A bare global
 * key would let the next operator to sign in on a shared machine see the
 * previous user's queued rows (filenames + match labels) on rehydrate —
 * before any team-scope check runs. The id segment binds the queue to
 * the `/api/me` user; `clearUploadQueueStorage()` wipes every variant at
 * the 401 sign-out boundary as defense-in-depth. */
const STORAGE_KEY_PREFIX = "tktic.upload-queue.v1.";

function storageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

/** Finished items (done/cancelled) older than this are dropped on
 * rehydrate so the panel doesn't accrete forever. */
const FINISHED_TTL_MS = 24 * 60 * 60 * 1000;

const TERMINAL: ReadonlySet<QueueItemStatus> = new Set([
  "done",
  "error",
  "cancelled",
]);

function isTerminal(status: QueueItemStatus): boolean {
  return TERMINAL.has(status);
}

/** Match a re-picked file against the queued item's recorded
 * fingerprint. Filename + size is the same cheap pre-check
 * `useChunkedUpload.discoverActiveSession` uses; the chunk-hash check
 * happens server-side when the upload actually starts. */
export function fileMatchesItem(item: QueueItem, file: File): boolean {
  return item.fileName === file.name && item.fileSize === file.size;
}

// --- Pure reducer-style transitions (exported for unit testing) ---

/** The next item the driver should start: the first `pending` item
 * that already has a `File`. Items awaiting a re-pick (file === null)
 * are skipped, not blocking — a later item with a file still runs. */
export function nextRunnable(items: QueueItem[]): QueueItem | null {
  return (
    items.find((it) => it.status === "pending" && it.file !== null) ?? null
  );
}

export function hasActive(items: QueueItem[]): boolean {
  return items.some((it) => it.status === "uploading");
}

export function setItemStatus(
  items: QueueItem[],
  id: string,
  status: QueueItemStatus,
  error: string | null = null,
): QueueItem[] {
  return items.map((it) =>
    it.id === id ? { ...it, status, error, updatedAt: Date.now() } : it,
  );
}

/** Drop done/cancelled items older than the TTL. Errored items are
 * kept (the operator should see + retry them) regardless of age. */
export function pruneFinished(
  items: QueueItem[],
  now: number = Date.now(),
): QueueItem[] {
  return items.filter((it) => {
    if (it.status !== "done" && it.status !== "cancelled") return true;
    return now - it.updatedAt < FINISHED_TTL_MS;
  });
}

/** Serialize for localStorage — strip the File. */
export function serializeQueue(items: QueueItem[]): StoredQueueItem[] {
  return items.map(({ file: _file, ...rest }) => rest);
}

/** Rehydrate from a parsed localStorage payload. Every item comes back
 * with `file: null`; a mid-upload item is demoted to `pending` so the
 * driver re-runs it once its file is supplied. Finished items past the
 * TTL are pruned. */
export function rehydrateQueue(
  raw: unknown,
  now: number = Date.now(),
): QueueItem[] {
  if (!Array.isArray(raw)) return [];
  const items: QueueItem[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Partial<StoredQueueItem>;
    if (
      typeof e.id !== "string" ||
      typeof e.matchId !== "string" ||
      typeof e.matchLabel !== "string" ||
      (e.slot !== "full" &&
        e.slot !== "first_half" &&
        e.slot !== "second_half" &&
        e.slot !== "extra_time" &&
        e.slot !== "penalty_shootout") ||
      typeof e.fileName !== "string" ||
      typeof e.fileSize !== "number" ||
      typeof e.updatedAt !== "number"
    ) {
      continue;
    }
    const status: QueueItemStatus =
      e.status === "uploading" || e.status === "pending"
        ? "pending"
        : e.status === "done" ||
            e.status === "error" ||
            e.status === "cancelled"
          ? e.status
          : "pending";
    items.push({
      id: e.id,
      matchId: e.matchId,
      teamId: typeof e.teamId === "string" ? e.teamId : undefined,
      seasonId: typeof e.seasonId === "string" ? e.seasonId : undefined,
      matchLabel: e.matchLabel,
      slot: e.slot,
      file: null,
      fileName: e.fileName,
      fileSize: e.fileSize,
      status,
      error: typeof e.error === "string" ? e.error : null,
      updatedAt: e.updatedAt,
    });
  }
  return pruneFinished(items, now);
}

function readStoredQueue(
  userId: string | null,
  now: number = Date.now(),
): QueueItem[] {
  if (!userId) return [];
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    return rehydrateQueue(JSON.parse(raw), now);
  } catch {
    return [];
  }
}

function writeStoredQueue(userId: string | null, items: QueueItem[]): void {
  if (!userId) return;
  try {
    window.localStorage.setItem(
      storageKey(userId),
      JSON.stringify(serializeQueue(items)),
    );
  } catch {
    // localStorage quota / private-browsing fallback: silent. Worst
    // case is no cross-reload resume — same rationale as the per-session
    // store in useChunkedUpload.
  }
}

/** Remove every per-user upload-queue entry from localStorage. Called at
 * the `/api/me` 401 boundary (sign-out / session loss) so a prior
 * session's queued filenames + match labels don't outlive their
 * visibility context on a shared machine. */
export function clearUploadQueueStorage(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) toRemove.push(key);
    }
    for (const key of toRemove) window.localStorage.removeItem(key);
  } catch {
    // Same private-browsing / quota fallback rationale as writeStoredQueue.
  }
}

function matchLabelFor(match: Match): string {
  const teams = `${match.home_team || "—"} vs ${match.away_team || "—"}`;
  return match.date ? `${teams} · ${match.date}` : teams;
}

export interface UploadQueueApi {
  items: QueueItem[];
  /** Live progress of the currently-uploading item (or idle state). */
  activeState: ChunkedUploadState;
  /** id of the item currently uploading, or null. */
  activeId: string | null;
  enqueue: (match: Match, slot: UploadSlot, file: File) => void;
  removeItem: (id: string) => void;
  retryItem: (id: string) => void;
  repickFile: (id: string, file: File) => boolean;
  cancelActive: () => void;
  clearFinished: () => void;
}

export function useUploadQueue(): UploadQueueApi {
  const queryClient = useQueryClient();
  // The queue's localStorage is scoped to this user's id. Reading the
  // store here keeps the queue bound to the signed-in operator — a
  // different user signing in on the same tab gets their own (empty)
  // queue, not the previous user's rows.
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const [items, setItems] = useState<QueueItem[]>(() =>
    readStoredQueue(userId),
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const { state: activeState, start, cancel } = useChunkedUpload();

  // Mirror items into a ref so the driver effect can read the latest
  // list without taking `items` as a dep (which would re-run the driver
  // — and thus potentially re-fire start() — on every list mutation).
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Re-hydrate from the new user's storage when the signed-in user
  // changes within the same tab (sign-out → sign-in). The initial
  // useState ran before the auth store hydrated on first mount; this
  // catches up once `/api/me` resolves. `userId === null` (signed out)
  // clears the in-memory list — `clearUploadQueueStorage()` at the 401
  // boundary handles the localStorage side.
  const lastUserIdRef = useRef(userId);
  useEffect(() => {
    if (lastUserIdRef.current === userId) return;
    lastUserIdRef.current = userId;
    setItems(readStoredQueue(userId));
    setActiveId(null);
  }, [userId]);

  // Persist on every change, keyed to the current user.
  useEffect(() => {
    writeStoredQueue(userId, items);
  }, [userId, items]);

  const invalidateMatches = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: unscopedKey("public-matches"),
      }),
      queryClient.invalidateQueries({ queryKey: unscopedKey("admin-matches") }),
      queryClient.invalidateQueries({ queryKey: ["admin-matches-scoped"] }),
      queryClient.invalidateQueries({ queryKey: ["coach-matches"] }),
    ]);
  }, [queryClient]);

  // Driver: when nothing is uploading, start the next runnable item.
  useEffect(() => {
    if (activeId !== null) return;
    if (hasActive(items)) return;
    const next = nextRunnable(items);
    if (!next || !next.file) return;

    setActiveId(next.id);
    setItems((prev) => setItemStatus(prev, next.id, "uploading"));
    void start(next.file, {
      matchId: next.matchId,
      slot: next.slot,
      teamId: next.teamId,
      seasonId: next.seasonId,
    }).catch(() => {
      // Terminal handling is driven by the activeState effect below;
      // the hook already records the error in its own state. Swallow
      // here so an unhandled rejection doesn't surface.
    });
  }, [activeId, items, start]);

  // React to the active upload reaching a terminal phase.
  useEffect(() => {
    if (activeId === null) return;
    if (activeState.phase === "done") {
      setItems((prev) => setItemStatus(prev, activeId, "done"));
      setActiveId(null);
      void invalidateMatches();
    } else if (activeState.phase === "error") {
      setItems((prev) =>
        setItemStatus(prev, activeId, "error", activeState.error),
      );
      setActiveId(null);
    } else if (activeState.phase === "cancelled") {
      setItems((prev) => setItemStatus(prev, activeId, "cancelled"));
      setActiveId(null);
    }
  }, [activeState.phase, activeState.error, activeId, invalidateMatches]);

  const enqueue = useCallback((match: Match, slot: UploadSlot, file: File) => {
    const item: QueueItem = {
      id:
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `q-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      matchId: match.id,
      teamId: match.team_id ?? undefined,
      seasonId: match.season_id ?? undefined,
      matchLabel: matchLabelFor(match),
      slot,
      file,
      fileName: file.name,
      fileSize: file.size,
      status: "pending",
      error: null,
      updatedAt: Date.now(),
    };
    setItems((prev) => [...prev, item]);
  }, []);

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const retryItem = useCallback((id: string) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === id && it.status === "error"
          ? { ...it, status: "pending", error: null, updatedAt: Date.now() }
          : it,
      ),
    );
  }, []);

  const repickFile = useCallback((id: string, file: File): boolean => {
    const item = itemsRef.current.find((it) => it.id === id);
    if (!item || !fileMatchesItem(item, file)) return false;
    setItems((prev) =>
      prev.map((it) =>
        it.id === id
          ? {
              ...it,
              file,
              status: isTerminal(it.status) ? "pending" : it.status,
              error: null,
              updatedAt: Date.now(),
            }
          : it,
      ),
    );
    return true;
  }, []);

  const cancelActive = useCallback(() => {
    cancel();
    // The activeState effect flips the item to `cancelled` once the
    // hook reports phase "cancelled".
  }, [cancel]);

  const clearFinished = useCallback(() => {
    setItems((prev) => prev.filter((it) => !isTerminal(it.status)));
  }, []);

  return {
    items,
    activeState,
    activeId,
    enqueue,
    removeItem,
    retryItem,
    repickFile,
    cancelActive,
    clearFinished,
  };
}
